import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from './useWorkspace';
import { useAuth } from './useAuth';
import { isGcalMultiCalendarEnabled } from '@/lib/gcal-feature-flags';

export interface GoogleCalendarCredential {
  id: string;
  email: string;
  is_active: boolean;
  needs_reconnect: boolean;
  calendar_id: string | null;
  calendar_display_name: string | null;
}

export interface GoogleSyncCalendar {
  id: string;
  credential_id: string;
  calendar_id: string;
  display_name: string | null;
  is_enabled: boolean;
  is_primary: boolean;
}

export interface GoogleCalendarConnection extends GoogleCalendarCredential {
  syncCalendars: GoogleSyncCalendar[];
}

interface UseGoogleCalendarsResult {
  connections: GoogleCalendarConnection[];
  enabledCount: number;
  isLoading: boolean;
  refresh: () => Promise<void>;
  /** Compat legado: primeira credential ativa ou null */
  primaryConnection: GoogleCalendarConnection | null;
  hasConnection: boolean;
  needsReconnect: boolean;
}

export function useGoogleCalendars(): UseGoogleCalendarsResult {
  const { user } = useAuth();
  const { workspaceId, workspaceMetadata } = useWorkspace(user?.id);
  const [connections, setConnections] = useState<GoogleCalendarConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!workspaceId) {
      setConnections([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data: credentials, error: credError } = await supabase
        .from('google_calendar_credentials')
        .select('id, email, is_active, needs_reconnect, calendar_id, calendar_display_name')
        .eq('workspace_id', workspaceId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (credError || !credentials?.length) {
        setConnections([]);
        return;
      }

      // Sempre carrega sync calendars para pickers (agente/produto).
      // A flag multi só controla UX (allowlist vs select único), não a listagem.
      const { data: syncData } = await supabase
        .from('google_calendar_sync_calendars')
        .select('id, credential_id, calendar_id, display_name, is_enabled, is_primary')
        .eq('workspace_id', workspaceId)
        .order('is_primary', { ascending: false })
        .order('created_at', { ascending: true });

      const syncCalendars = (syncData ?? []) as GoogleSyncCalendar[];

      const mapped: GoogleCalendarConnection[] = credentials.map((cred) => ({
        ...(cred as GoogleCalendarCredential),
        syncCalendars: syncCalendars.filter((sc) => sc.credential_id === cred.id),
      }));

      setConnections(mapped);
    } catch (error) {
      console.error('Error loading Google calendars:', error);
      setConnections([]);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, workspaceMetadata]);

  useEffect(() => {
    void load();
  }, [load]);

  const primaryConnection = connections[0] ?? null;
  const enabledCount = connections.reduce(
    (sum, conn) => sum + conn.syncCalendars.filter((sc) => sc.is_enabled).length,
    0,
  );

  return {
    connections,
    enabledCount: isGcalMultiCalendarEnabled(workspaceMetadata)
      ? enabledCount
      : (primaryConnection ? 1 : 0),
    isLoading,
    refresh: load,
    primaryConnection,
    hasConnection: connections.length > 0,
    needsReconnect: connections.some((c) => c.needs_reconnect === true),
  };
}
